import type { TChipTone } from '@umodeapporg/ui/components/UI/Chips/chipTones';
import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import type { TFunction } from 'i18next';

import { formatMinutesAsMmss } from '@/app/sequencias/helpers/tempo';

/**
 * The ONE ruler every screen reads the comparison on: words, never a multiplier or a
 * percentage. "Promete cedo" means the engine reserves less time than the floor spends,
 * so the promised date comes out earlier than the factory can deliver; "promete tarde"
 * is the opposite and costs sales.
 */
export type TVeredito = 'PROMETE_CEDO' | 'PROMETE_TARDE' | 'DENTRO_DO_PREVISTO';

/** Red for the date nobody keeps, orange for the sale lost, green inside tolerance. */
export const VEREDITO_TONE: Record<TVeredito, TChipTone> = {
  PROMETE_CEDO: 'danger',
  PROMETE_TARDE: 'warning',
  DENTRO_DO_PREVISTO: 'success',
};

export const VEREDITO_ICON: Record<TVeredito, SymbolCodepoints> = {
  PROMETE_CEDO: 'trending_down',
  PROMETE_TARDE: 'trending_up',
  DENTRO_DO_PREVISTO: 'check_circle',
};

/**
 * Which side of the tolerance the comparison falls on, by the SAME arithmetic the server
 * uses to light up Divergente: the distance from 1 of "engine time over floor time"
 * against the tolerated deviation configured in Settings. `null` when there is nothing
 * to compare (no time in force, no sample), which is NOT the same as "inside tolerance".
 */
export function resolveVeredito(razao: number | null, tolerancia: number): TVeredito | null {
  if (razao === null || !Number.isFinite(razao) || razao <= 0) {
    return null;
  }
  if (razao > 1 + tolerancia) {
    return 'PROMETE_TARDE';
  }
  if (razao < 1 - tolerancia) {
    return 'PROMETE_CEDO';
  }
  return 'DENTRO_DO_PREVISTO';
}

/**
 * How many times the engine's time is the floor's, from the two times themselves. Used
 * where the API sends the pair and not the ratio (the attention queue); the curation
 * sends its own ratio, and that one is passed through untouched so the sentence and the
 * status chip can never disagree.
 */
export function razaoMotorFabrica(motor: number | null, fabrica: number | null): number | null {
  if (motor === null || fabrica === null || motor <= 0 || fabrica <= 0) {
    return null;
  }
  return motor / fabrica;
}

/** A minutes per piece reading as mm:ss, or `null` when there is no time at all. */
export function minPecaLabel(minutos: number | null): string | null {
  return minutos !== null && minutos > 0 ? formatMinutesAsMmss(minutos) : null;
}

/** A fraction as a whole percentage, for the parameter values quoted in the explanations. */
export function fractionToPercent(fracao: number): number {
  return Math.round(fracao * 100);
}

/** One decimal, the same reading the pace already has on the apontamento screen. */
export function formatRitmo(pecasHora: number): string {
  return pecasHora.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** People can be a weighted average (two cells), so it reads with up to one decimal. */
export function formatPessoas(pessoas: number): string {
  return pessoas.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

/**
 * The house plural shape (`key` / `keyPlural`), the same one the queue uses: a sample
 * line that says "1 apontamentos" gives the whole screen away.
 */
export function pluralKey(base: string, total: number): string {
  return total === 1 ? base : `${base}Plural`;
}

/**
 * "baseado em 1 OP · 12 apontamentos": how representative the sample is, which is part of
 * the claim and never a detail. `null` when nothing was tracked, since "0 OPs" would be
 * noise on top of the line that already says there is nothing to compare. `ops` is left
 * out where the comparison is about a single OP and the count would say nothing.
 */
export function baseAmostraLabel(t: TFunction, apontamentos: number, ops?: number): string | null {
  if (apontamentos <= 0 && (ops ?? 0) <= 0) {
    return null;
  }
  const apontamentosLabel = t(pluralKey('explica.apontamentos', apontamentos), { apontamentos });
  const partes =
    ops === undefined ? apontamentosLabel : `${t(pluralKey('explica.ops', ops), { ops })} · ${apontamentosLabel}`;
  return t('explica.base', { partes });
}
